/**
 * SSO Login Options Component
 * Displays available SSO login buttons (Google, Microsoft, Apple)
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { useAuth } from '../../../contexts/AuthContext';
import { apiService } from '../../../services/api';
import GoogleLoginButton from '../../atoms/GoogleLoginButton/GoogleLoginButton';
import MicrosoftLoginButton from '../../atoms/MicrosoftLoginButton/MicrosoftLoginButton';
import AppleLoginButton from '../../atoms/AppleLoginButton/AppleLoginButton';
import { MICROSOFT_CONFIG, OAUTH_PROVIDERS } from '../../../config/oauth.config';
import './SSOLoginOptions.scss';

// Initialize MSAL instance for Microsoft
const msalInstance = MICROSOFT_CONFIG.clientId
  ? new PublicClientApplication({
      auth: {
        clientId: MICROSOFT_CONFIG.clientId,
        authority: MICROSOFT_CONFIG.authority,
        redirectUri: MICROSOFT_CONFIG.redirectUri,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
      },
    })
  : null;

interface SSOLoginOptionsProps {
  onSuccess?: () => void;
  onError?: (error: any) => void;
  showDivider?: boolean;
  dividerText?: string;
}

const SSOLoginOptions: React.FC<SSOLoginOptionsProps> = ({
  onSuccess,
  onError,
  showDivider = true,
  dividerText = 'o inicia sesión con correo',
}) => {
  const { loginWithGoogle, loginWithMicrosoft, loginWithApple } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleGoogleSuccess = async (token: string) => {
    setLoading(true);
    try {
      // First, try to login with existing user
      try {
        await loginWithGoogle(token);
        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/');
        }
        return;
      } catch (loginError: any) {
        // If login fails, check if it's because user needs to accept terms
        if (loginError.response?.status === 403) {
          // Get user info and redirect to terms acceptance
          const userInfo = await apiService.verifyUserInfo(token, 'google');
          navigate('/sso-terms', { state: { userInfo } });
          return;
        }
        throw loginError;
      }
    } catch (error) {
      console.error('Google login error:', error);
      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftSuccess = async (token: string) => {
    setLoading(true);
    try {
      // First, try to login with existing user
      try {
        await loginWithMicrosoft(token);
        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/');
        }
        return;
      } catch (loginError: any) {
        // If login fails, check if it's because user needs to accept terms
        if (loginError.response?.status === 403) {
          // Get user info and redirect to terms acceptance
          const userInfo = await apiService.verifyUserInfo(token, 'microsoft');
          navigate('/sso-terms', { state: { userInfo } });
          return;
        }
        throw loginError;
      }
    } catch (error) {
      console.error('Microsoft login error:', error);
      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSuccess = async (response: any) => {
    setLoading(true);
    try {
      // Apple response contains authorization code and potentially user data
      const idToken = response.authorization?.id_token;
      const userData = response.user;
      
      if (idToken) {
        // First, try to login with existing user
        try {
          await loginWithApple(idToken, userData);
          if (onSuccess) {
            onSuccess();
          } else {
            navigate('/');
          }
          return;
        } catch (loginError: any) {
          // If login fails, check if it's because user needs to accept terms
          if (loginError.response?.status === 403) {
            // Get user info and redirect to terms acceptance
            const userInfo = await apiService.verifyUserInfo(idToken, 'apple');
            navigate('/sso-terms', { state: { userInfo } });
            return;
          }
          throw loginError;
        }
      }
    } catch (error) {
      console.error('Apple login error:', error);
      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleError = (error: any) => {
    console.error('SSO login error:', error);
    if (onError) {
      onError(error);
    }
  };

  // Check if any OAuth provider is configured
  const hasAnyProvider = Object.values(OAUTH_PROVIDERS).some((configured) => configured);

  if (!hasAnyProvider) {
    return null;
  }

  return (
    <div className="sso-login-options">
      <div className="sso-buttons">
        {OAUTH_PROVIDERS.google && (
          <GoogleLoginButton
            onSuccess={handleGoogleSuccess}
            onError={handleError}
            disabled={loading}
          />
        )}

        {OAUTH_PROVIDERS.microsoft && msalInstance && (
          <MsalProvider instance={msalInstance}>
            <MicrosoftLoginButton
              onSuccess={handleMicrosoftSuccess}
              onError={handleError}
              disabled={loading}
            />
          </MsalProvider>
        )}

        {OAUTH_PROVIDERS.apple && (
          <AppleLoginButton
            onSuccess={handleAppleSuccess}
            onError={handleError}
            disabled={loading}
          />
        )}
      </div>

      {showDivider && (
        <div className="divider">
          <span>{dividerText}</span>
        </div>
      )}
    </div>
  );
};

export default SSOLoginOptions;


